import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Delete from "@/components/common/alerts/Delete";
import Success from "@/components/common/alerts/Success";
import { Modal } from "@/config/antd";
import { CloseOutlined } from "@/config/antd";
import Avatar from "@/components/common/Avatar";
import WebIcon from "@/components/common/Web_Icons";
import Button from "@/components/common/Button";
import { useLoader } from "@/hooks/LoaderProvider";
import MediaDialog from "@/pages/media_manager/components/Media_Dialog";
import * as pdfjsLib from "pdfjs-dist";

pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const SingleMediaPhoto = ({
  isVisible,
  onClose,
  mediaData,
  isEditMode = false,
}) => {
  const { showLoader, hideLoader } = useLoader();

  // Add fallback for both title and name
  const title = mediaData?.title || mediaData?.name || "";
  const description = mediaData?.description || "";
  const date = mediaData?.date || mediaData?.createdAt || "";
  const author = mediaData?.author || mediaData?.userID || "";
  const authorAvatar = mediaData?.authorAvatar || null;

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);

  useEffect(() => {
    if (isEditMode) {
      setShowEditDialog(true);
    }
  }, [isEditMode]);

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = () => {
    onClose();
    setShowDeleteModal(false);
    showLoader();
    setTimeout(() => {
      hideLoader();
      setShowSuccessModal(true);
    }, 2000);
  };

  const handleSuccessClose = () => {
    setShowSuccessModal(false);
    onClose();
    showLoader();
    setTimeout(() => {
      hideLoader();
    }, 2000);
  };

  const handleEdit = () => {
    setShowEditDialog(true);
  };

  const handleDownload = async () => {
    const fileUrl =
      mediaData?.imageFiles?.[0] ||
      mediaData?.videoFiles?.[0] ||
      mediaData?.documentFiles?.[0];

    if (!fileUrl) return;

    try {
      showLoader();
      
      // Use a hidden anchor with the download attribute as a first attempt
      // For images/PDFs, we fetch them as blobs to force the browser to download instead of opening
      const response = await fetch(fileUrl, {
        mode: 'cors',
        credentials: 'omit'
      });
      
      if (!response.ok) throw new Error('Network response was not ok');
      
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      
      // Extract filename
      const filename = fileUrl.split("/").pop() || title || "download";
      link.download = filename;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download failed, falling back to direct link:", error);
      // Fallback: If fetch is blocked by CORS, we use a different approach
      // We'll create a link and try to force download via the 'download' attribute
      const link = document.createElement('a');
      link.href = fileUrl;
      link.target = '_blank';
      const filename = fileUrl.split("/").pop() || title || "download";
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } finally {
      hideLoader();
    }
  };

  const renderLeftPanel = () => {
    // Determine which files exist
    const hasImages = mediaData?.imageFiles?.length > 0;
    const hasVideos = mediaData?.videoFiles?.length > 0;
    const hasDocuments = mediaData?.documentFiles?.length > 0;

    if (hasImages) {
      return (
        <>
          {/* Main Image */}
          <div className="w-full h-[400px] bg-gray-100 rounded-lg overflow-hidden mb-4">
            <img
              src={mediaData?.imageFiles?.[0] || ""}
              alt={title}
              className="object-cover w-full h-full"
            />
          </div>

          {/* Image Grid */}
          <div className="grid grid-cols-4 gap-2 mb-6">
            {mediaData?.imageFiles?.slice(1)?.map((image, index) => (
              <div
                key={index}
                className="overflow-hidden rounded-lg aspect-square">
                <img
                  src={image}
                  alt={`Image ${index + 1}`}
                  className="object-cover w-full h-full"
                />
              </div>
            ))}
          </div>
        </>
      );
    } else if (hasVideos) {
      return (
        <div className="w-full h-[500px] bg-gray-100 rounded-lg overflow-hidden mb-4">
          <video controls className="w-full h-full" crossOrigin="anonymous">
            <source src={mediaData?.videoFiles?.[0] || ""} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
      );
    } else if (hasDocuments) {
      const docUrl = mediaData?.documentFiles?.[0] || "";
      const isPdf = docUrl.toLowerCase().endsWith(".pdf");

      return (
        <div className="w-full h-[500px] bg-gray-100 rounded-lg overflow-hidden mb-4">
          {isPdf ? (
            <iframe
              src={`${docUrl}#view=FitH`}
              title="PDF Preview"
              width="100%"
              height="100%"
              className="border-none"
            />
          ) : (
            <div className="flex flex-col items-center justify-center h-full p-6">
              <WebIcon
                icon="DocumentUpload"
                size="large"
                className="w-16 h-16 text-primary mb-4"
              />
              <p className="text-regular text-center mb-4">
                This file type cannot be previewed directly.
              </p>
              <Button
                text="DOWNLOAD TO VIEW"
                onClick={handleDownload}
                className="border border-primary"
                customBgColor="white"
                customTextColor="primary"
              />
            </div>
          )}
        </div>
      );
    }

    // Fallback if no files are found
    return (
      <div className="w-full h-[400px] bg-gray-100 rounded-lg flex items-center justify-center mb-4">
        <WebIcon
          icon="DocumentUpload"
          size="large"
          className="w-[48px] h-[48px] text-gray-400"
        />
        <p className="text-gray-500 mt-4">No preview available</p>
      </div>
    );
  };

  if (!mediaData) return null;

  return (
    <>
      <Modal
        open={isVisible}
        onCancel={onClose}
        footer={null}
        width={1200}
        centered
        closeIcon={
          <div className="px-6 py-2 border rounded-lg border-primary mr-14">
            <CloseOutlined className="text-xl" />
          </div>
        }
        className="single-media-modal">
        <div className="flex flex-col gap-4 lg:flex-row">
          <div className="flex-1">{renderLeftPanel()}</div>

          <div className="flex-1 lg:mt-12">
            {/* Title and Description */}
            <div className="mb-6">
              <h1 className="text-[28px] font-bold text-lightestGrey mb-4">
                {title}
              </h1>
              <div className="w-full h-[10px] bg-secondary mb-4"></div>
              <p className="text-regular text-lighterGrey">{description}</p>
            </div>

            {/* Author and Date Info */}
            <div className="flex items-center justify-between border-b border-[#E5E7EB] py-4 mb-6">
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-3">
                  <Avatar
                    size={40}
                    src={authorAvatar}
                    className="border-2 border-gray-100"
                  />
                  <h3 className="text-bold-x text-lightestGrey">{author}</h3>
                </div>
                <div className="flex items-center gap-2 ml-2">
                  <WebIcon icon="Calendar2" size="large" />
                  <span className="text-small text-lighterGrey">{date}</span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between">
              <div className="flex gap-2">
                <button
                  className="flex items-center gap-2 text-primary hover:underline"
                  onClick={handleEdit}>
                  <WebIcon icon="Edit" size="large" />
                </button>
                <button
                  className="flex items-center gap-2 text-primary hover:underline"
                  onClick={handleDelete}>
                  <WebIcon icon="Trash" size="large" />
                </button>
              </div>

              <div className="flex gap-2 items-right">
                <Button
                  text="DOWNLOAD"
                  onClick={handleDownload}
                  icon={
                    <WebIcon
                      icon="DocumentDownload"
                      size="large"
                      className="w-[18px] h-[18px]"
                    />
                  }
                  iconPosition="left"
                  customBgColor="white"
                  customTextColor="primary"
                  className="border border-primary gap-2 h-[40px] font-normal text-small"
                />
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* Edit Dialog */}
      <MediaDialog
        isVisible={showEditDialog}
        onClose={() => setShowEditDialog(false)}
        mediaData={mediaData}
      />

      {/* Delete Modal */}
      <Delete
        isVisible={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onProceed={handleDeleteConfirm}
        header="Are you sure?"
        paragraph="Are you sure you want to remove this file from your file manager list."
        proceedButtonText="Proceed"
        cancelButtonText="Cancel"
      />

      {/* Success Modal */}
      <Success
        isVisible={showSuccessModal}
        onClose={handleSuccessClose}
        header="File Deleted"
        paragraph="You have successfully removed this file from your file manager list."
        buttonText="Close"
      />
    </>
  );
};

SingleMediaPhoto.propTypes = {
  isVisible: PropTypes.bool,
  onClose: PropTypes.func,
  mediaData: PropTypes.shape({
    title: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    date: PropTypes.string,
    createdAt: PropTypes.string,
    author: PropTypes.string,
    userID: PropTypes.string,
    authorAvatar: PropTypes.string,
    mainImage: PropTypes.string,
    imageFiles: PropTypes.arrayOf(PropTypes.string),
    childImages: PropTypes.arrayOf(PropTypes.string),
    videoFiles: PropTypes.arrayOf(PropTypes.string),
    documentFiles: PropTypes.arrayOf(PropTypes.string),
  }),
  isEditMode: PropTypes.bool,
};

export default SingleMediaPhoto;
